import { useMemo } from 'react';
import { useQuery, UseQueryOptions } from 'react-query';
import { MS_IN_SECOND } from '@distribusion/portal-lib/constants';
import { Partner } from '@distribusion/portal-lib/types';
import { USER_MANAGEMENT_BASE_API_PATH } from 'constants/common';
import axios, { AxiosError, AxiosResponse } from 'lib/axios';

export enum PermissionKeyEnum {
  VIEW_SALES_DASHBOARD = 'view_sales_dashboard',
  VIEW_ANCILLARY_INSIGHTS = 'view_ancillary_insights',
  VIEW_TICKET_VALIDATOR = 'view_ticket_validator',
  VIEW_PASSENGER_MANIFEST = 'view_passenger_manifest',
  VIEW_PAYPAL_COMMISSION = 'view_paypal_commission',
  EDIT_WHITELABEL_SETTINGS = 'edit_whitelabel_settings',
  VIEW_CUSTOMER_DATA = 'view_customer_data',
  VIEW_TRADING_DESK = 'view_trading_desk',
  VIEW_SLA = 'view_sla',
  VIEW_PARTNER_SETTINGS = 'view_partner_settings',
  VIEW_GLOBAL_INVENTORY = 'view_global_inventory',
  VIEW_SERVICE_DESK = 'view_service_desk',
  VIEW_GUIDELINES = 'view_guidelines',
  VIEW_USERS = 'view_users',
  VIEW_BOOK_A_TRIP = 'view_book_a_trip',
  VIEW_PAYMENT_INFORMATION = 'view_payment_information',
  VIEW_DEPARTURE_DEEP_DIVE = 'view_departure_deep_dive',
  VIEW_HOME_PAGE_CAPABILITIES = 'view_home_page_capabilities',
  VIEW_WHITELABEL_CUSTOMIZATION_PAGE = 'view_whitelabel_customization_page',
  VIEW_HOME_PAGE_ACCOUNT_MANAGER = 'view_home_page_account_manager',
  VIEW_HOME_PAGE_SALES = 'view_home_page_sales',
  VIEW_BOOKINGS = 'view_bookings',
  VIEW_PRICING = 'view_pricing',
  VIEW_API_DOCS = 'view_api_docs',
  VIEW_RETAILER_TAB = 'view_retailer_tab',
  VIEW_HOME_PAGE_NEWS = 'view_home_page_news',
  VIEW_RESERVATIONS_PAGE = 'view_reservations_page',
  VIEW_CANCELLATIONS_PAGE = 'view_cancellations_page',
  VIEW_BOOKINGS_CANCELLATIONS_TAB = 'view_bookings_cancellations_tab',
  VIEW_HOME_PAGE = 'view_home_page',
  VIEW_FINANCE = 'view_finance',
  MANAGE_SUBSCRIPTIONS = 'manage_subscriptions',
  MANAGE_USER_ACCOUNTS = 'manage_user_accounts',
  EDIT_AGENCY_MANAGEMENT = 'edit_agency_management',
  VIEW_PROTOTYPES = 'view_prototypes',
  CREATE_INTERNAL_USERS = 'create_internal_users',
  CREATE_EXTERNAL_USERS = 'create_external_users',
}

export interface ParticularPermissionAccess {
  allow: boolean;
  particular: Record<Partner['code'], Partner>;
}

export interface PermissionAccess {
  allow: boolean;
  carriers: ParticularPermissionAccess;
  retailers: ParticularPermissionAccess;
}

export type PermissionsAccessMap = Record<PermissionKeyEnum, PermissionAccess>;

const PERMISSIONS_STALE_TIME_MS = 30 * MS_IN_SECOND;
const REFETCH_PERMISSIONS_INTERVAL_MS = 30 * MS_IN_SECOND;

export const permissionsKeys = {
  all: ['common', 'users', 'permissions'] as const,
};

const getPermissions = () =>
  axios.get<PermissionsAccessMap>(`${USER_MANAGEMENT_BASE_API_PATH}/v2/permissions/sets`);

export const useGetPermissionsDetails = (options?: Pick<UseQueryOptions, 'enabled'>) =>
  useQuery<AxiosResponse<PermissionsAccessMap>, AxiosError, PermissionsAccessMap>(
    permissionsKeys.all,
    getPermissions,
    {
      select: (res) => res.data,
      retry: false,
      refetchOnMount: false,
      staleTime: PERMISSIONS_STALE_TIME_MS,
      refetchInterval: REFETCH_PERMISSIONS_INTERVAL_MS,
      ...options,
    },
  );

export const usePermissions = () => {
  const permissionsQuery = useGetPermissionsDetails();

  const data = useMemo(() => {
    const accessMap = permissionsQuery.data || ({} as PermissionsAccessMap);
    return Object.entries(accessMap).reduce(
      (acc, [key, access]) => ({ ...acc, [key]: access.allow }),
      {} as Record<PermissionKeyEnum, boolean>,
    );
  }, [permissionsQuery.data]);

  return useMemo(() => ({ ...permissionsQuery, data }), [data, permissionsQuery]);
};
